import React from 'react'

import { buildComponent } from 'src/components/factory'
import { useAudioConstraints } from 'src/hooks/use-audio-constraints'
import { useAudioConfiguration } from 'src/hooks/use-audio-configuration'
import { useStoreState } from 'src/hooks/state'
import { useResponsive } from 'src/hooks/use-responsive'
import { Image } from 'src/components/elements/image/image'
import { Category } from 'src/state/models/audio'

import * as UI from 'native-base'
import * as Semantics from 'src/components/semantics'

export const ConfiguredAudio = buildComponent<{
  large?: boolean
  imageOnly?: boolean
  textOnly?: string
  configuration?: Parameters<typeof useAudioConstraints>[0]
}>()
  .withLocale('components.elements.configured-audio')
  .withLifecycle(({ props, locale }) => {
    const { theme, book, background } = useAudioConstraints(props.configuration)
    const configuration = useAudioConfiguration()
    const categories = useStoreState(
      (store) => store.space.info?.subscription?.audio?.categories ?? [],
    )
    const { width } = useResponsive()

    const themeCover = theme
      ? `images/themes/${theme.slug}/${theme.cover ?? 'cover'}.png`
      : null
    const bookCover = book
      ? `images/books/${book.slug}/${book.cover ?? 'cover'}.png`
      : null
    const coverImage =
      themeCover ?? bookCover ?? 'images/themes/music-only/cover.png'

    const themeTitle = theme?.title
    const bookTitle = book?.title?.long

    // Récupérer le titre de la catégorie si disponible
    const getCategoryTitle = () => {
      if (configuration.category) {
        const selectedCategory = categories.find(
          (cat: Category) => cat.slug === configuration.category,
        )
        if (selectedCategory && selectedCategory.type !== 'background') {
          return (
            selectedCategory.long[configuration.locale] ||
            selectedCategory.long.fr ||
            selectedCategory.short[configuration.locale] ||
            selectedCategory.short.fr
          )
        } else if (selectedCategory) {
          return (
            selectedCategory?.short[configuration.locale] ||
            selectedCategory?.short.fr ||
            locale('musicOnly')
          )
        }
      }
      return null
    }

    const categoryTitle = getCategoryTitle()
    const title =
      themeTitle ?? bookTitle ?? categoryTitle ?? locale('musicOnly')

    const imageSize = props.imageOnly
      ? props.large
        ? 96
        : 56
      : props.large
      ? 64
      : 48

    return { theme, book, background, coverImage, title, width, imageSize }
  })
  .withRender(({ props, lifecycle }) => {
    return (
      <UI.Row
        px={props.imageOnly || props.textOnly ? 0 : 4}
        alignItems="center"
        justifyContent="center"
      >
        {!props.textOnly && (
          <UI.Box
            borderColor="primary.900"
            backgroundColor="white"
            borderWidth={3}
            width={lifecycle.imageSize}
            height={lifecycle.imageSize}
            margin={props.imageOnly ? 0 : 4}
            borderRadius={4}
            overflow="hidden"
          >
            <UI.Box
              width="100%"
              height="100%"
              borderRadius={3}
              overflow="hidden"
            >
              <Image
                aspectRatio={1}
                resizeMode="cover"
                source={lifecycle.coverImage}
              />
            </UI.Box>
          </UI.Box>
        )}

        {!props.imageOnly && (
          <UI.Column
            maxWidth={
              lifecycle.width - (props.large && props.textOnly ? 64 : 128)
            }
            overflow="hidden"
            marginRight={props.textOnly ? 0 : 8}
          >
            {props.textOnly && (
              <UI.Text numberOfLines={1} color="secondary.700" fontSize="xs">
                {props.textOnly}
              </UI.Text>
            )}

            <UI.Text
              numberOfLines={1}
              color="white"
              overflow="hidden"
              ellipsizeMode="tail"
              fontSize={props.textOnly ? (props.large ? 'md' : '2xs') : 'xs'}
              mb={props.textOnly ? -0.5 : 0}
              textAlign={props.large && props.textOnly ? 'center' : 'left'}
            >
              <Semantics.Localized>{lifecycle.title}</Semantics.Localized>
            </UI.Text>

            {lifecycle.background && (
              <UI.Text
                numberOfLines={1}
                color="white"
                opacity={0.5}
                fontSize={props.textOnly ? (props.large ? 'sm' : '3xs') : 'xs'}
                textAlign={props.large && props.textOnly ? 'center' : 'left'}
              >
                <Semantics.Localized>
                  {lifecycle.background.long}
                </Semantics.Localized>
              </UI.Text>
            )}
          </UI.Column>
        )}
      </UI.Row>
    )
  })
